import momentTimezone from 'moment-timezone';
import moment from 'moment/moment';
import React from 'react';
import { getDirectionVariantsByRoute, getDirectionVariantsForStop, getRoutesPairs } from '../../actions/gtfsStaticActions';
import { getPositions, getSchedule, getStopPredictions, getStops } from '../../actions/vehiclesHistoryActions';
import { VehiclePositionState } from '../../types/busHistoryTypes';
import { DirectionVariant, GtfsStop2 } from '../../types/gtfsTypes';
import { Dictionary } from '../../types/view-models-interfaces';
import { ActionType, BusPositionHistoryAction } from './types/ActionType';
import { AgencyModel } from './types/agencyModel';
import { BphDirectionsState, BphRoutesState, BphSliderState, DateTimeState, StopPredictionsState } from './types/bphStates';
import { BphDirection, BphDirectionModel, BphRouteModel, BphStopModel, BphStopsStateType, BphUrlFiltersState, BphVehicleModel, BusHistoryUrlApplyStage, BusHistoryUrlFilterKey, BusHistoryUrlFilters, BusPositionHistoryStateType, ParsedBusHistoryUrlFilters } from './types/BphTypes';
import { DateTime } from './types/dateTime';
import { DirectionsMapDisplayPool } from './types/DirectionsMapDisplayPool';
import { InternalInstanceHelper, PublicInstanceHelper } from './types/InternalInstanceHelper';
import { Optional } from './types/Optional';
import { SliderInfoProps as BphSliderInfoProps } from './types/SliderInfoProps';
import { StopsMapDisplayPool } from './types/StopsMapDisplayPool';
import { VehicleMapDisplayPool } from './types/VehicleMapDisplayPool';

export const initialState: BusPositionHistoryStateType = {
    helper: (undefined as unknown) as PublicInstanceHelper,
    mapReady: false,
    agency: null,
    dateTime: {} as DateTimeState,
    routesLoading: false,
    routesState: {
        routes: null,
        selectedRoute: null,
        initialRoutesList: [],
    },
    directionsLoading: false,
    directionsState: {
        directions: null,
        selectedDirection: null,
    },
    directionsInfo: [],
    stopsLoading: false,
    stopsState: {
        stops: null,
        selectedStop: null,
        loadingSchedule: false,
    },
    stopsSearchState: {},
    stopsNoResultMessageState: undefined,
    stopsPredictions: null,
    historyLoading: false,
    busPositionsState: [],
    scheduleDates: null,
    sliderOptions: null,
    sliderInfo: null,
    displayDirectionStops: false,
    mapsBounds: null,
    vehicles: null,
    urlFiltersState: {
        initialized: false,
        applying: false,
        stage: 'idle',
        sourceFilters: null,
        pendingFilters: null,
        droppedKeys: [],
    },
};

export function composeForRoute(directionVariant: DirectionVariant) {
    return `${directionVariant.directionVariantName}`;
}

export function composeForStop(directionVariant: DirectionVariant) {
    return `${directionVariant.routeName} ${directionVariant.directionVariantName}`;
}

const urlFilterKeys: BusHistoryUrlFilterKey[] = ['date', 'time', 'routeId', 'stopId', 'directionVariantId', 'displayStops'];

function getFormattedDateTime(date: string, time: string): string {
    return `${date} ${time}`;
}

function getInitialDateTime(): DateTimeState {
    const dt = moment(new Date().setMinutes(0, 0, 0));
    const date = dt.format('YYYY-MM-DD');
    const time = dt.format('h:mm A');
    return {
        selectedDate: date,
        selectedTime: time,
        selectedDateTime: getFormattedDateTime(date, time),
        initializedFromBrowser: true,
    };
}

function getAgencyInitialDateTime(agency: AgencyModel): DateTimeState {
    const timeZoneName = agency.timeZoneName || 'America/Chicago';
    const dt = momentTimezone.tz(timeZoneName).minutes(0).seconds(0).milliseconds(0);
    const date = dt.format('YYYY-MM-DD');
    const time = dt.format('h:mm A');
    return {
        selectedDate: date,
        selectedTime: time,
        selectedDateTime: getFormattedDateTime(date, time),
        initializedFromBrowser: false,
    };
}

function hasUrlDateTimeFilters(state: BusPositionHistoryStateType): boolean {
    const source = state.urlFiltersState.sourceFilters;
    const pending = state.urlFiltersState.pendingFilters;
    return !!(source?.date || source?.time || pending?.date || pending?.time);
}

export function initializeState(src: BusPositionHistoryStateType): BusPositionHistoryStateType {
    if (!src.helper) {
        const result: BusPositionHistoryStateType = {
            ...src,
            helper: new InstanceHelper(),
            dateTime: getInitialDateTime(),
        };
        return result;
    }
    return src;
}

export function cast(state: BusPositionHistoryStateType): InternalInstanceHelper {
    if (state.helper instanceof InstanceHelper) {
        return state.helper;
    }

    console.error('BusPositionHistoryContext: invalid state.helper');
    return (state.helper as unknown) as InternalInstanceHelper;
}

export class InstanceHelper implements PublicInstanceHelper, InternalInstanceHelper {
    static nextUid = 0;

    public readonly uid: number;
    public dispatch: React.Dispatch<BusPositionHistoryAction>;
    public _map?: any;

    private _selectedDateRequestId = 0;
    private _stopAndDirectionRequestId = 0;
    private readonly _spool: StopsMapDisplayPool;
    private readonly _tpool: DirectionsMapDisplayPool;
    private readonly _vpool: VehicleMapDisplayPool;

    constructor() {
        this.uid = ++InstanceHelper.nextUid;
        this.dispatch = (() => null) as any;
        this._spool = new StopsMapDisplayPool(this);
        this._vpool = new VehicleMapDisplayPool(this);
        this._tpool = new DirectionsMapDisplayPool(this);
    }

    public onMapReady(): void {
        this.dispatch({ type: ActionType.SET_MAP_READY, payload: null });
    }

    public async setAgency(state: BusPositionHistoryStateType, agency: AgencyModel | null) {
        if (state.agency === agency) return;
        if (state.agency?.id && agency?.id && state.agency.id === agency.id) return;

        if (agency && state.dateTime.initializedFromBrowser && !hasUrlDateTimeFilters(state)) {
            this.dispatch({
                type: ActionType.SET_DATE_TIME,
                payload: getAgencyInitialDateTime(agency),
            });
        }

        this.dispatch({ type: ActionType.SET_AGENCY, payload: agency });
        return true;
    }

    private _filtersEquals(left: Nullable<BusHistoryUrlFilters>, right: Nullable<BusHistoryUrlFilters>): boolean {
        if (left === right) {
            return true;
        }

        if (!left || !right) {
            return false;
        }

        return urlFilterKeys.every((key: BusHistoryUrlFilterKey) => left[key] === right[key]);
    }

    private _droppedKeysEquals(left: BusHistoryUrlFilterKey[], right: BusHistoryUrlFilterKey[]): boolean {
        if (left.length !== right.length) {
            return false;
        }

        return left.every((value: BusHistoryUrlFilterKey, index: number) => value === right[index]);
    }

    private _normalizePendingFilters(value: Nullable<BusHistoryUrlFilters>): Nullable<BusHistoryUrlFilters> {
        if (!value) {
            return null;
        }

        const normalized: BusHistoryUrlFilters = {};

        if (value.date) {
            normalized.date = value.date;
        }
        if (value.time) {
            normalized.time = value.time;
        }
        if (value.routeId) {
            normalized.routeId = value.routeId;
        }
        if (value.stopId) {
            normalized.stopId = value.stopId;
        }
        if (value.directionVariantId) {
            normalized.directionVariantId = value.directionVariantId;
        }
        if (value.displayStops !== undefined) {
            normalized.displayStops = value.displayStops;
        }

        return Object.keys(normalized).length > 0 ? normalized : null;
    }

    private _mergeDroppedKeys(base: BusHistoryUrlFilterKey[], extra: BusHistoryUrlFilterKey[]): BusHistoryUrlFilterKey[] {
        if (extra.length === 0) {
            return base;
        }

        const set = new Set<BusHistoryUrlFilterKey>(base);
        for (const key of extra) {
            set.add(key);
        }
        return urlFilterKeys.filter((key: BusHistoryUrlFilterKey) => set.has(key));
    }

    private _setUrlFiltersState(state: BusPositionHistoryStateType, value: BphUrlFiltersState): void {
        const current: BphUrlFiltersState = state.urlFiltersState;
        if (current.initialized === value.initialized
            && current.applying === value.applying
            && current.stage === value.stage
            && this._filtersEquals(current.sourceFilters, value.sourceFilters)
            && this._filtersEquals(current.pendingFilters, value.pendingFilters)
            && this._droppedKeysEquals(current.droppedKeys, value.droppedKeys)) {
            return;
        }

        this.dispatch({ type: ActionType.SET_URL_FILTERS_STATE, payload: value });
    }

    private _updateUrlFiltersState(
        state: BusPositionHistoryStateType,
        stage: BusHistoryUrlApplyStage,
        pendingFilters: Nullable<BusHistoryUrlFilters>,
        droppedKeys?: BusHistoryUrlFilterKey[],
    ): void {
        const normalizedPending: Nullable<BusHistoryUrlFilters> = this._normalizePendingFilters(pendingFilters);
        const nextStage: BusHistoryUrlApplyStage = normalizedPending ? stage : 'completed';
        const dropped: BusHistoryUrlFilterKey[] = this._mergeDroppedKeys(state.urlFiltersState.droppedKeys, droppedKeys || []);

        this._setUrlFiltersState(state, {
            ...state.urlFiltersState,
            initialized: true,
            applying: nextStage !== 'idle' && nextStage !== 'completed',
            stage: nextStage,
            pendingFilters: normalizedPending,
            droppedKeys: dropped,
        });
    }

    private _copyPendingFilters(pending: Nullable<BusHistoryUrlFilters>): BusHistoryUrlFilters {
        return {
            ...(pending || {}),
        };
    }

    public initializeUrlFilters(state: BusPositionHistoryStateType, value: Optional<ParsedBusHistoryUrlFilters>): void {
        if (state.urlFiltersState.initialized) {
            return;
        }

        if (!value || !value.hasSupportedParams) {
            this._setUrlFiltersState(state, {
                ...state.urlFiltersState,
                initialized: true,
                applying: false,
                stage: 'completed',
                sourceFilters: null,
                pendingFilters: null,
                droppedKeys: [],
            });
            return;
        }

        const pending: Nullable<BusHistoryUrlFilters> = this._normalizePendingFilters(value.filters);
        const stage: BusHistoryUrlApplyStage = pending ? 'dateTime' : 'completed';

        this._setUrlFiltersState(state, {
            ...state.urlFiltersState,
            initialized: true,
            applying: stage !== 'completed',
            stage: stage,
            sourceFilters: pending,
            pendingFilters: pending,
            droppedKeys: value.invalidKeys,
        });
    }

    public applyPendingUrlFilters(state: BusPositionHistoryStateType): void {
        if (!state.urlFiltersState.initialized || !state.urlFiltersState.applying) {
            return;
        }

        const pending: Nullable<BusHistoryUrlFilters> = state.urlFiltersState.pendingFilters;
        if (!pending) {
            this._updateUrlFiltersState(state, 'completed', null);
            return;
        }

        if (!state.agency) {
            return;
        }

        switch (state.urlFiltersState.stage) {
            case 'dateTime':
                this._applyUrlDateTimeStage(state, pending);
                return;
            case 'route':
                this._applyUrlRouteStage(state, pending);
                return;
            case 'stop':
                this._applyUrlStopStage(state, pending);
                return;
            case 'direction':
                this._applyUrlDirectionStage(state, pending);
                return;
            case 'idle':
            case 'completed':
            default:
                return;
        }
    }

    private _applyUrlDateTimeStage(state: BusPositionHistoryStateType, pending: BusHistoryUrlFilters): void {
        if (pending.date || pending.time) {
            this.updateDateTime(state, pending.date, pending.time);
        }

        const nextPending: BusHistoryUrlFilters = this._copyPendingFilters(pending);
        delete nextPending.date;
        delete nextPending.time;
        this._updateUrlFiltersState(state, 'route', nextPending);
    }

    private _applyUrlRouteStage(state: BusPositionHistoryStateType, pending: BusHistoryUrlFilters): void {
        const routeId: Optional<string> = pending.routeId;
        if (!routeId) {
            this._updateUrlFiltersState(state, 'stop', pending);
            return;
        }

        if (state.routesLoading || !state.routesState.routes) {
            return;
        }

        const nextPending: BusHistoryUrlFilters = this._copyPendingFilters(pending);
        const route = state.routesState.routes.find((item: BphRouteModel) => item.routeId === routeId) || null;
        if (route) {
            this.selectRoute(state, route);
        }
        else {
            const droppedKeys: BusHistoryUrlFilterKey[] = ['routeId'];
            if (!nextPending.stopId) {
                delete nextPending.directionVariantId;
                droppedKeys.push('directionVariantId');
            }
            delete nextPending.routeId;
            this._updateUrlFiltersState(state, 'stop', nextPending, droppedKeys);
            return;
        }

        delete nextPending.routeId;
        this._updateUrlFiltersState(state, 'stop', nextPending);
    }

    private _applyUrlStopStage(state: BusPositionHistoryStateType, pending: BusHistoryUrlFilters): void {
        const stopId: Optional<string> = pending.stopId;
        if (!stopId) {
            this._updateUrlFiltersState(state, 'direction', pending);
            return;
        }

        if (!state.routesState.selectedRoute && !state.stopsState.stops && !state.stopsLoading && state.stopsSearchState.searchQuery !== stopId) {
            this.setStopsSearchString(state, stopId);
            return;
        }

        if (state.stopsLoading || state.directionsLoading) {
            return;
        }

        if (!state.stopsState.stops) {
            if (state.routesState.selectedRoute) {
                if (state.directionsLoading || state.directionsState.directions === null) {
                    return;
                }
            }
            return;
        }

        const nextPending: BusHistoryUrlFilters = this._copyPendingFilters(pending);
        const stop = state.stopsState.stops.find((item: BphStopModel) => item.stopId === stopId) || null;
        if (stop) {
            this.selectStop(state, stop);
        }
        else {
            if (state.routesState.selectedRoute) {
                return;
            }
            this._updateUrlFiltersState(state, 'direction', {
                ...nextPending,
                stopId: undefined,
            }, ['stopId']);
            return;
        }

        delete nextPending.stopId;
        this._updateUrlFiltersState(state, 'direction', nextPending);
    }

    private _applyUrlDirectionStage(state: BusPositionHistoryStateType, pending: BusHistoryUrlFilters): void {
        const nextPending: BusHistoryUrlFilters = this._copyPendingFilters(pending);
        const droppedKeys: BusHistoryUrlFilterKey[] = [];

        if (pending.directionVariantId) {
            if (state.directionsLoading) {
                return;
            }

            const directions: Nullable<BphDirectionModel[]> = state.directionsState.directions;
            if (!directions) {
                if (!state.routesLoading && !state.stopsLoading) {
                    delete nextPending.directionVariantId;
                    droppedKeys.push('directionVariantId');
                    if (nextPending.displayStops !== undefined) {
                        delete nextPending.displayStops;
                        droppedKeys.push('displayStops');
                    }
                    this._updateUrlFiltersState(state, 'completed', nextPending, droppedKeys);
                }
                return;
            }

            const direction = directions.find((item: BphDirectionModel) => item.directionVariantId === pending.directionVariantId) || null;
            if (direction) {
                this.selectDirection(state, direction);
            } else {
                droppedKeys.push('directionVariantId');
            }
            delete nextPending.directionVariantId;
        }

        if (pending.displayStops !== undefined) {
            const selectedDirection = state.directionsState.selectedDirection;
            if (!selectedDirection) {
                if (!pending.directionVariantId) {
                    delete nextPending.displayStops;
                    droppedKeys.push('displayStops');
                }
                this._updateUrlFiltersState(state, 'direction', nextPending, droppedKeys);
                return;
            }

            if (state.displayDirectionStops !== pending.displayStops) {
                this.onDisplayDirectionStopsChanged(pending.displayStops);
            }
            delete nextPending.displayStops;
        }

        this._updateUrlFiltersState(state, 'completed', nextPending, droppedKeys);
    }

    public updateDateTime(state: BusPositionHistoryStateType, date: Optional<string>, time: Optional<string>): boolean {
        date = date || state.dateTime.selectedDate;
        time = time || state.dateTime.selectedTime;

        const dateChanged = date !== state.dateTime.selectedDate;
        const timeChanged = time !== state.dateTime.selectedTime;

        if (!dateChanged && !timeChanged) {
            return false;
        }

        const value: DateTimeState = {
            selectedDate: date,
            selectedTime: time,
            selectedDateTime: getFormattedDateTime(date, time),
            initializedFromBrowser: false,
        };
        this.dispatch({ type: ActionType.SET_DATE_TIME, payload: value });
        if (!dateChanged && timeChanged) {
            if (!state.routesState?.selectedRoute) {
                this._setStopsState(null);
            }
            this.selectRoute(state, null);
        }
        return true;
    }

    private _setMapBounds(value: any) {
        this.dispatch({ type: ActionType.SET_MAP_BOUNDS, payload: value });
    }

    private _setRoutesState(value: Optional<BphRoutesState>): void {
        this.dispatch({ type: ActionType.SET_ROUTES_STATE, payload: value || initialState.routesState });
    }

    public selectRoute(state: BusPositionHistoryStateType, value: Optional<BphRouteModel>): boolean {
        this._tpool.forceClear?.();
        this._spool.empty();
        this._setStopsState(null);
        value = value || null;
        if (state.routesState.selectedRoute === value) return false;
        this._setRoutesState({
            ...state.routesState,
            selectedRoute: value,
        });
        return true;
    }

    private _setDirections(value?: Optional<BphDirectionModel[]>): void {
        const v: BphDirectionsState = {
            directions: value || null,
            selectedDirection: null,
        };
        this.dispatch({ type: ActionType.SET_DIRECTIONS_STATE, payload: v });
        if (!value) {
            this._tpool.empty();
            this._vpool.empty();
        }
        else {
            const dirs: Dictionary<BphDirectionModel> = {};
            for (const d of value) {
                this._tpool.add(d);
                dirs[d.directionVariantId] = d;
            }
            this._tpool.set(dirs);
        }
    }

    public selectDirection(state: BusPositionHistoryStateType, value: Optional<BphDirectionModel>): boolean {
        value = value || null;
        if (state.directionsState.selectedDirection === value) return false;
        if (state.directionsState.selectedDirection) {
            state.directionsState.selectedDirection.select(false);
        }
        this._tpool.select(value || null);
        this.dispatch({
            type: ActionType.SET_DIRECTIONS_STATE,
            payload: {
                directions: state.directionsState.directions,
                selectedDirection: value,
            },
        });
        return true;
    }

    private _setStopsLoading(value: boolean): void {
        this.dispatch({ type: ActionType.SET_STOPS_LOADING, payload: !!value });
    }

    private _setStopsState(value: Optional<BphStopsStateType>): void {
        const v = value || initialState.stopsState;
        this.dispatch({ type: ActionType.SET_STOPS_STATE, payload: v });
        this._spool.select(v.selectedStop);
    }

    public selectStop(state: BusPositionHistoryStateType, value: Optional<BphStopModel>): boolean {
        value = value || null;
        if (state.stopsState.selectedStop === value) return false;
        this._spool.select(value);
        this._setStopsState({
            stops: state.stopsState.stops,
            selectedStop: value,
            loadingSchedule: false,
        });
        return true;
    }

    public setStopsSearchString(state: BusPositionHistoryStateType, value: Optional<string>): void {
        if (state.routesState.selectedRoute) {
            return;
        }
        if (!value) {
            this._setStopsNoResutMessageState('');
        }
        this.dispatch({
            type: ActionType.SET_STOP_SEARCH_STATE,
            payload: { searchQuery: value },
        });
    }

    private _setStopsNoResutMessageState(value: Optional<string>): void {
        this.dispatch({ type: ActionType.SET_NO_STOPS_MESSSAGE, payload: value || null });
    }

    private _setDirectionsInfo(value: Optional<BphDirection[]>): void {
        this.dispatch({ type: ActionType.SET_DIRECTIONS_INFO, payload: value || initialState.directionsInfo });
    }

    private _setStopPredictions(value: Optional<StopPredictionsState>): void {
        this.dispatch({ type: ActionType.SET_STOPS_PREDICTIONS, payload: value || initialState.stopsPredictions });
    }

    private _setBusPositionsState(value: Optional<VehiclePositionState[]>): void {
        this.dispatch({ type: ActionType.SET_BUS_POSITIONS, payload: value || initialState.busPositionsState });
    }

    private _setScheduleDates(value: Optional<DateTime[]>): void {
        this.dispatch({ type: ActionType.SET_SCHEDULE_DATES, payload: value || null });
    }

    private _setSliderOptions(value: Optional<BphSliderState>): void {
        this.dispatch({ type: ActionType.SET_SLIDER_OPTIONS, payload: value || null });
    }

    private _setSliderInfo(value: Optional<BphSliderInfoProps>): void {
        this._vpool.highlight(value?.vehiclePosition.vehicleId);
        this.dispatch({ type: ActionType.SET_SLIDER_INFO, payload: value || null });
    }

    private _setRoutesLoading(value: boolean) {
        this.dispatch({ type: ActionType.SET_ROUTES_LOADING, payload: value });
    }

    public async onSelectedDateChanged(state: BusPositionHistoryStateType) {
        const requestId = ++this._selectedDateRequestId;
        this._setRoutesState(null);
        this._setStopsState(null);
        this._setDirections(null);
        this._setDirectionsInfo(null);
        this._setBusPositionsState(null);

        this._tpool.empty();
        this._vpool.empty();

        if (!state.agency || !state.dateTime.selectedDate) return;

        this._setRoutesLoading(true);

        const serviceDate = moment(state.dateTime.selectedDate).format('YYYY-MM-DD');
        try {
            const resRoutes = await getRoutesPairs(state.agency.id, serviceDate);
            if (requestId !== this._selectedDateRequestId) {
                return;
            }
            this._setRoutesState({
                routes: resRoutes.map(item => new BphRouteModel(item.key, item.value)),
                selectedRoute: null,
                initialRoutesList: resRoutes.map(item => new BphRouteModel(item.key, item.value)),
            });
        }
        finally {
            if (requestId === this._selectedDateRequestId) {
                this._setRoutesLoading(false);
            }
        }
    }

    private _setDirectionsLoading(value: boolean) {
        this.dispatch({ type: ActionType.SET_DIRECTIONS_LOADING, payload: value });
    }

    private _setHistoryLoading(value: boolean) {
        this.dispatch({ type: ActionType.SET_HISTORY_LOADING, payload: value });
    }

    private async _updateSliderData(agencyId: string, selectedDateState: string, direction: Nullable<BphDirectionModel>) {
        if (!agencyId || !direction) return;

        this._vpool.empty();
        this._setHistoryLoading(true);
        try {
            const time = moment(selectedDateState).format('YYYY-MM-DDTHH:mm:ss');
            const vehiclePositions = await getPositions(agencyId, direction.directionVariantId, time);
            this._setBusPositionsState([...vehiclePositions ?? []]);
        }
        finally {
            this._setHistoryLoading(false);
        }
    }

    public async onSelectedRouteChanged(state: BusPositionHistoryStateType) {
        this._tpool.empty();
        if (!state.stopsState.selectedStop) {
            this._setDirections(null);
            this._setDirectionsInfo(null);
            this._setBusPositionsState(null);
            this._setScheduleDates(null);
        }

        if (!state.agency) {
            return;
        }

        if (state.routesState.selectedRoute) {
            try {
                this._setDirectionsLoading(true);
                const directionVariants = state.stopsState.selectedStop?.stopId
                    ? await getDirectionVariantsForStop(state.agency.id, state.stopsState.selectedStop.stopId, state.routesState.selectedRoute.routeId, state.dateTime.selectedTime, state.dateTime.selectedDate)
                    : await getDirectionVariantsByRoute(state.routesState.selectedRoute.routeId, state.dateTime.selectedTime, state.dateTime.selectedDate);
                if (directionVariants) {
                    this._showDirections(directionVariants, composeForRoute);
                    if (!state.stopsState.selectedStop) {
                        const directionStops = directionVariants.reduce<GtfsStop2[]>((accumulator, direction) => accumulator.concat(direction.stops), []);
                        this._loadStopsOptions(directionStops);
                    }
                }
            }
            finally {
                this._setDirectionsLoading(false);
            }
        }
    }

    public async onSelectedStopChanged(state: BusPositionHistoryStateType) {
        if (!state.routesState.selectedRoute) {
            this._setDirections(null);
            this._setDirectionsInfo(null);
            this._setBusPositionsState(null);
            this._setScheduleDates(null);
        }
        if (!state.agency) {
            return;
        }

        this._spool.select(state.stopsState.selectedStop);

        if (!state.stopsState.selectedStop) {
            this._setRoutesState({
                ...state.routesState,
                routes: state.routesState.initialRoutesList,
                selectedRoute: state.routesState.initialRoutesList.find(r => r.routeId === state?.routesState?.selectedRoute?.routeId) || null,
            });
            this.onSelectedRouteChanged(state);
        }
        if (state.routesState.selectedRoute && state.directionsState.selectedDirection) {
            return;
        }
        if (!state.stopsState.selectedStop && !state.routesState.selectedRoute) {
            this._tpool.empty();
            this._setDirectionsInfo(null);
        }
        if (state.stopsState.selectedStop) {
            this._tpool.empty();
            this._setDirections(null);
            let directionVariantId: Nullable<string> = null;
            try {
                this._setDirectionsLoading(true);
                const directionVariants = await getDirectionVariantsForStop(
                    state.agency.id,
                    state.stopsState.selectedStop.stopId,
                    state.routesState.selectedRoute?.routeId,
                    state.dateTime.selectedTime,
                    state.dateTime.selectedDate,
                );
                if (directionVariants) {
                    const directionsForRoutes = state?.routesState?.selectedRoute?.displayName
                        ? directionVariants.filter(d => d.routeName === state?.routesState?.selectedRoute?.displayName)
                        : directionVariants;
                    this._showDirections(directionsForRoutes, composeForStop);
                    directionVariantId = directionsForRoutes[0]?.directionVariantId ?? null;
                    if ((state.routesState.routes?.length ?? 0) > 0 && !state.routesState.selectedRoute) {
                        const routes = state.routesState.initialRoutesList.filter(r => directionVariants.map(v => v.routeName).includes(r.displayName));
                        this._setRoutesState({
                            ...state.routesState,
                            routes,
                            selectedRoute: routes.find(r => r.routeId === state?.routesState?.selectedRoute?.routeId) || null,
                        });
                    }
                }
            }
            finally {
                this._setDirectionsLoading(false);
                if (state.directionsState.directions && directionVariantId) {
                    this.selectDirection(state, state.directionsState.directions.find(d => d.directionVariantId === directionVariantId));
                }
            }
        }
    }

    public async onSelectedDirectionChanged(state: BusPositionHistoryStateType) {
        if (!state.agency) return;

        this._setBusPositionsState(null);
        this._setSliderInfo(null);

        const isDisplayDirectionStops = this._spool.isDisplayDirectionStops;
        if (isDisplayDirectionStops) {
            this._spool.switchDirectionStops(false);
        }
        if (state.directionsState.selectedDirection) {
            this._setMapBounds(state.directionsState.selectedDirection.bounds);
            this._spool.setDirectionStops(state.directionsState.selectedDirection.getStops());
            if (isDisplayDirectionStops) {
                this._spool.switchDirectionStops(true);
            }
        } else {
            this._spool.setDirectionStops([]);
        }

        await this._updateSliderData(state.agency.id, state.dateTime.selectedDateTime, state.directionsState.selectedDirection);
    }

    public async onStopSearchParamsChanged(state: BusPositionHistoryStateType) {
        if (state.agency && state.stopsSearchState?.searchQuery) {
            this._setStopsLoading(true);
            try {
                const result = await getStops(
                    state.agency.id,
                    state.stopsSearchState.searchQuery,
                    state.directionsState.selectedDirection?.directionVariantId,
                    state.routesState.selectedRoute?.routeId,
                );
                if (result) {
                    this._loadStopsOptions(result);
                }
            }
            finally {
                this._setStopsLoading(false);
            }
        }
    }

    public async onStopAndDirectionChanged(state: BusPositionHistoryStateType): Promise<void> {
        const requestId = ++this._stopAndDirectionRequestId;
        this._setStopPredictions(null);
        this._setScheduleDates(null);

        const stopId = state.stopsState.selectedStop?.stopId;
        const selectedDirection = state.directionsState.selectedDirection;

        if (!state.agency || !selectedDirection || !stopId) return;

        this._setStopsState({ ...state.stopsState, loadingSchedule: true });

        const serviceDate = DateTime.fromStringAgencyDateTime(state.dateTime.selectedDateTime);
        const directionVariantId = selectedDirection.directionVariantId;

        const schedulePromise = this._loadScheduleForStopAndDirection(
            requestId,
            state,
            serviceDate.format('YYYY-MM-DD'),
            stopId,
            directionVariantId,
        );
        const predictionsPromise = this._loadStopPredictionsForStopAndDirection(
            requestId,
            state.agency.id,
            stopId,
            directionVariantId,
            serviceDate.format('YYYY-MM-DDTHH:mm:ss'),
        );

        await Promise.allSettled([schedulePromise, predictionsPromise]);
    }

    private async _loadScheduleForStopAndDirection(
        requestId: number,
        state: BusPositionHistoryStateType,
        serviceDate: string,
        stopId: string,
        directionVariantId: string,
    ): Promise<void> {
        try {
            const resSchedule = await getSchedule(serviceDate, stopId, directionVariantId);
            if (!this._isLatestStopAndDirectionRequest(requestId)) {
                return;
            }

            if (resSchedule) {
                const scheduleRange = resSchedule
                    .map(s => DateTime.fromStringWithZoneAsAgency(s));
                this._setScheduleDates(scheduleRange);
            } else {
                this._setScheduleDates(null);
            }
        }
        catch (_error) {
            if (!this._isLatestStopAndDirectionRequest(requestId)) {
                return;
            }

            console.error('BusHistory: getSchedule failed', _error);
            this._setScheduleDates(null);
        }
        finally {
            if (this._isLatestStopAndDirectionRequest(requestId)) {
                this._setStopsState({ ...state.stopsState, loadingSchedule: false });
            }
        }
    }

    private async _loadStopPredictionsForStopAndDirection(
        requestId: number,
        agencyId: string,
        stopId: string,
        directionVariantId: string,
        serviceDateTime: string,
    ): Promise<void> {
        try {
            const resPredictions = await getStopPredictions(agencyId, stopId, directionVariantId, serviceDateTime);
            if (!this._isLatestStopAndDirectionRequest(requestId)) {
                return;
            }

            this._setStopPredictions(new StopPredictionsState(resPredictions));
        }
        catch (_error) {
            if (!this._isLatestStopAndDirectionRequest(requestId)) {
                return;
            }

            console.error('BusHistory: getStopPredictions failed', _error);
            this._setStopPredictions(null);
        }
    }

    private _isLatestStopAndDirectionRequest(requestId: number): boolean {
        return this._stopAndDirectionRequestId === requestId;
    }

    public onSliderTimeChanged(state: BusPositionHistoryStateType, sliderValue: number): void {
        if (state.busPositionsState.length === 0) {
            this._setSliderInfo(null);
            return;
        }

        const aheadPosition = this._onTimeSliderChange(state.busPositionsState, sliderValue);
        const position = state.busPositionsState[sliderValue];

        if (state.directionsState.selectedDirection && position) {
            const selectedDirection = state.directionsState.selectedDirection;
            this._setSliderInfo({
                routeName: selectedDirection.routeName,
                stopName: selectedDirection.lastStop,
                vehiclePosition: position,
                aheadPosition,
            });
        }
    }

    private setDisplyDirectionStops(value: boolean) {
        this.dispatch({ type: ActionType.DISPLY_DIRECTION_STOPS, payload: value });
    }

    public onDisplayDirectionStopsChanged(value: boolean): void {
        this._spool.switchDirectionStops(value);
        this.setDisplyDirectionStops(value);
    }

    public onBusPositionsChanged(state: BusPositionHistoryStateType): void {
        this._vpool.empty();

        if (state.busPositionsState.length > 0) {
            const sliderValue = Math.floor(state.busPositionsState.length / 2);
            this._setSliderOptions({
                maxValue: state.busPositionsState.length - 1,
                sliderValue,
            });
            this.onSliderTimeChanged(state, sliderValue);
        } else {
            this._setSliderOptions(null);
        }
    }

    private _showDirections(directionVariants: DirectionVariant[], nameComposer: (v: DirectionVariant) => string) {
        let bounds: Nullable<{ north: number; south: number; east: number; west: number }> = null;
        const infos: BphDirection[] = [];
        const directions: BphDirectionModel[] = [];
        for (const variant of directionVariants) {
            const direction = new BphDirectionModel(
                nameComposer(variant),
                variant,
            );
            directions.push(direction);
            infos.push({
                id: variant.directionVariantId,
                route: variant.routeName,
                stop: variant.lastStop,
            });
            bounds = bounds === null
                ? { ...direction.bounds }
                : {
                    north: Math.max(bounds.north, direction.bounds.north),
                    south: Math.min(bounds.south, direction.bounds.south),
                    east: Math.max(bounds.east, direction.bounds.east),
                    west: Math.min(bounds.west, direction.bounds.west),
                };
        }
        if (bounds) {
            this._setMapBounds(bounds);
        }
        this._setDirectionsInfo(infos);
        this._setDirections(directions);
    }

    private _loadStopsOptions(stops: GtfsStop2[]) {
        if (stops.length === 1 && !stops[0].stopId) {
            this._setStopsState(null);
            this._setStopsNoResutMessageState(stops[0].stopName);
        } else {
            const directionStops = Array.from(new Set(stops.map(s => {
                const r = JSON.stringify({
                    stopId: s.stopId,
                    displayName: s.stopName,
                    lat: s.lat,
                    lon: s.lon,
                });
                return r;
            }))).map(s => JSON.parse(s));
            this._setStopsState({
                stops: directionStops.map(s => new BphStopModel(s.displayName, s.stopId, s.lat, s.lon)),
                selectedStop: null,
                loadingSchedule: false,
            });
        }
    }

    private _onTimeSliderChange(
        busPositionsState: VehiclePositionState[],
        sliderValue: number,
    ): VehiclePositionState | null {
        const vehicles: Dictionary<BphVehicleModel> = {};
        const position = busPositionsState[sliderValue];

        this._addVehicle(vehicles, position);

        const ahead: VehiclePositionState | null = null;
        const maxTripDuration = 5 * 60000;
        const lowerBoundTime = moment.parseZone(position.reportTimeAtz).valueOf() - maxTripDuration / 2;
        const upperBoundTime = moment.parseZone(position.reportTimeAtz).valueOf() + maxTripDuration / 2;

        for (let ix = sliderValue + 1; ix < busPositionsState.length; ++ix) {
            const sibling = busPositionsState[ix];
            if (moment.parseZone(sibling.reportTimeAtz).valueOf() > upperBoundTime) {
                break;
            }
            if (!(sibling.vehicleId in vehicles)) {
                this._addVehicle(vehicles, sibling);
            }
        }

        for (let ix = sliderValue - 1; ix >= 0; --ix) {
            const sibling = busPositionsState[ix];
            if (moment.parseZone(sibling.reportTimeAtz).valueOf() < lowerBoundTime) {
                break;
            }
            if (!(sibling.vehicleId in vehicles)) {
                this._addVehicle(vehicles, sibling);
            }
        }

        this._vpool.set(vehicles);
        this.dispatch({ type: ActionType.SET_VEHICLES, payload: vehicles });

        return ahead;
    }

    private _addVehicle(
        vehicles: Dictionary<BphVehicleModel>,
        position: VehiclePositionState,
    ) {
        let vehicle: BphVehicleModel;
        if (this._vpool.has(position.vehicleId)) {
            vehicle = this._vpool.get(position.vehicleId);
            vehicle.update(position);
        }
        else {
            vehicle = new BphVehicleModel(position);
            this._vpool.add(vehicle);
        }
        vehicles[vehicle.id] = vehicle;
    }
}
